import * as echarts from 'echarts/core';
import {chartColor, CHART_COLORS} from './bid-dashboard-theme';

const TITLES = {
  scale: '规模分布',
  type: '类型分布',
  result: '开标结果',
  trend: '月度趋势',
};

function formatWan(value) {
  const n = Number(value) || 0;
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return `${n.toLocaleString()}元`;
}

function pick(obj, keys, fallback) {
  for (let i = 0; i < keys.length; i++) {
    const val = obj[keys[i]];
    if (val != null) return val;
  }
  return fallback;
}

function pieOption(items, title) {
  const list = items || [];
  const total = list.reduce((s, v) => s + (Number(v.value) || 0), 0);
  const data = list.length
    ? list.map((item, i) => ({
      name: item.name,
      value: item.value,
      itemStyle: {color: chartColor(i)},
    }))
    : [{name: '暂无数据', value: 1, itemStyle: {color: '#e8eef5'}, tooltip: {show: false}}];
  const showLegend = list.length > 0 && list.length <= 5;
  const empty = !list.length;

  return {
    title: {
      text: title,
      left: 10,
      top: 8,
      textStyle: {fontSize: 12, fontWeight: 700, color: '#15263d'},
    },
    legend: showLegend ? {
      bottom: 4,
      left: 'center',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 6,
      textStyle: {fontSize: 10, color: '#6a7f99'},
    } : {show: false},
    tooltip: {
      trigger: 'item',
      confine: true,
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#d7e2ef',
      textStyle: {fontSize: 11, color: '#15263d'},
      formatter: empty ? () => '暂无数据' : '{b}: {c} ({d}%)',
    },
    series: [{
      type: 'pie',
      radius: showLegend ? ['42%', '64%'] : ['48%', '72%'],
      center: ['50%', showLegend ? '46%' : '56%'],
      avoidLabelOverlap: true,
      itemStyle: {
        borderRadius: 4,
        borderColor: '#fff',
        borderWidth: 2,
      },
      label: {show: false},
      labelLine: {show: false},
      silent: empty,
      data,
    }],
    graphic: [{
      type: 'text',
      left: 'center',
      top: showLegend ? '38%' : '48%',
      style: {
        text: empty ? '—' : `${total}`,
        textAlign: 'center',
        fill: '#0f3d73',
        fontSize: 16,
        fontWeight: 800,
      },
    }, {
      type: 'text',
      left: 'center',
      top: showLegend ? '50%' : '60%',
      style: {
        text: empty ? '暂无' : '项',
        textAlign: 'center',
        fill: '#9db2c9',
        fontSize: 10,
      },
    }],
  };
}

function trendComboOption(items, title) {
  const list = items || [];
  const months = list.length ? list.map((v) => v.name) : ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
  const bidCounts = list.length ? list.map((v) => pick(v, ['bid_count', 'bid', 'value'], 0)) : months.map(() => 0);
  const winCounts = list.length ? list.map((v) => pick(v, ['win_count', 'win'], 0)) : months.map(() => 0);
  const winAmounts = list.length ? list.map((v) => Number(pick(v, ['win_amount', 'amount'], 0)) / 10000) : months.map(() => 0);

  return {
    title: {
      text: title,
      left: 8,
      top: 2,
      textStyle: {fontSize: 12, fontWeight: 700, color: '#15263d'},
    },
    legend: {
      top: 6,
      right: 10,
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 10,
      textStyle: {fontSize: 10, color: '#6a7f99'},
      data: ['参与投标数', '中标数', '中标金额'],
    },
    grid: {left: 40, right: 44, top: 36, bottom: 24},
    tooltip: {
      trigger: 'axis',
      confine: true,
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#d7e2ef',
      textStyle: {fontSize: 11},
      formatter(params) {
        const lines = [`<b>${params[0].axisValue}</b>`];
        params.forEach((p) => {
          if (p.seriesName === '中标金额') {
            lines.push(`${p.marker}${p.seriesName}: ${formatWan(p.data * 10000)}`);
          } else {
            lines.push(`${p.marker}${p.seriesName}: ${p.data} 项`);
          }
        });
        return lines.join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: months,
      axisLine: {lineStyle: {color: '#e8eef5'}},
      axisTick: {show: false},
      axisLabel: {fontSize: 10, color: '#9db2c9', margin: 8},
    },
    yAxis: [{
      type: 'value',
      name: '项',
      nameTextStyle: {fontSize: 10, color: '#9db2c9', padding: [0, 24, 0, 0]},
      axisLine: {show: false},
      axisTick: {show: false},
      axisLabel: {fontSize: 10, color: '#9db2c9'},
      splitLine: {lineStyle: {color: '#eef3f9', type: 'dashed'}},
    }, {
      type: 'value',
      name: '万元',
      nameTextStyle: {fontSize: 10, color: '#9db2c9', padding: [0, 0, 0, 24]},
      axisLine: {show: false},
      axisTick: {show: false},
      axisLabel: {fontSize: 10, color: '#9db2c9'},
      splitLine: {show: false},
    }],
    series: [{
      name: '参与投标数',
      type: 'line',
      yAxisIndex: 0,
      smooth: 0.35,
      symbol: 'circle',
      symbolSize: 4,
      showSymbol: false,
      lineStyle: {width: 2, color: '#1f6feb'},
      itemStyle: {color: '#1f6feb'},
      data: bidCounts,
    }, {
      name: '中标数',
      type: 'line',
      yAxisIndex: 0,
      smooth: 0.35,
      symbol: 'circle',
      symbolSize: 4,
      showSymbol: false,
      lineStyle: {width: 2, color: '#c9780c'},
      itemStyle: {color: '#c9780c'},
      data: winCounts,
    }, {
      name: '中标金额',
      type: 'bar',
      yAxisIndex: 1,
      barWidth: '36%',
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {offset: 0, color: 'rgba(95, 158, 235, 0.9)'},
          {offset: 1, color: 'rgba(15, 61, 115, 0.28)'},
        ]),
        borderRadius: [3, 3, 0, 0],
      },
      data: winAmounts,
    }],
  };
}

export function buildChartOption(type, items) {
  const list = items || [];
  const title = TITLES[type] || '';

  if (['scale', 'type', 'result'].includes(type)) {
    return pieOption(list, title);
  }
  if (type === 'trend') {
    return trendComboOption(list, title);
  }
  return pieOption(list, title);
}

export {TITLES, CHART_COLORS};
